import { Overlay, OverlayConfig, OverlayModule, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
    ChangeDetectionStrategy,
    Component,
    inject,
    OnDestroy,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';
import { FuiDestroyService } from '@services/destroy.service';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-popup-content',
    templateUrl: './popup-content.component.html',
    imports: [OverlayModule],
    providers: [FuiDestroyService],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})

/** Компонент всплывающего контента */
export class PopupContentComponent implements OnDestroy {
    readonly #destroy$ = inject(FuiDestroyService);

    /** Шаблон для добавления в портал */
    readonly #viewContainerRef: ViewContainerRef = inject(ViewContainerRef);

    /** CDK Оверлей */
    readonly #overlay: Overlay = inject(Overlay);

    /** Ссылка на CDK Оверлей */
    private overlayRef: OverlayRef | null = null;

    /** Ссылка на шаблон */
    @ViewChild('content', { static: true }) content: TemplateRef<any>;

    /** Отобразить контент (координаты начала отображения)*/
    show(x: number, y: number): void {
        this.createOverlay(x, y);
    }

    /** Скрыть контент */
    hide(): void {
        this.destroyOverlay();
    }

    /** Уничтожение компонента */
    ngOnDestroy(): void {
        this.destroyOverlay();
    }

    /** Создание оверлея */
    private createOverlay(x: number, y: number): void {
        const positionStrategy = this.#overlay
            .position()
            .flexibleConnectedTo({ x, y } as any)
            .withPositions([
                {
                    originX: 'start',
                    originY: 'top',
                    overlayX: 'start',
                    overlayY: 'top',
                },
            ]);

        const overlayConfig = new OverlayConfig({
            positionStrategy,
            scrollStrategy: this.#overlay.scrollStrategies.reposition(), //  Перепозиционирование при прокрутке
            hasBackdrop: true, //  Добавляем backdrop
            backdropClass: 'cdk-overlay-transparent-backdrop', //  Прозрачный backdrop
        });

        this.overlayRef = this.#overlay.create(overlayConfig);

        const templatePortal = new TemplatePortal(this.content, this.#viewContainerRef);

        this.overlayRef.attach(templatePortal);

        //  Подписываемся на клики по backdrop для закрытия
        this.overlayRef
            .backdropClick()
            .pipe(takeUntil(this.#destroy$))
            .subscribe(() => this.hide());
    }

    /** Уничтожение оверлея */
    private destroyOverlay(): void {
        if (this.overlayRef) {
            this.overlayRef.dispose();
            this.overlayRef = null;
        }
    }
}
